import type {
  FormPage,
  FormQuestion,
  JumpCondition,
  JumpLogicEvaluator,
} from "@kushya/shared";

type AnswerRecord = Record<string, unknown>;

export function findPageIndexForQuestion(
  questionRef: string,
  pageIds: string[],
  questions: FormQuestion[],
): number | null {
  const question = questions.find((candidate) => candidate.ref === questionRef);
  if (!question) {
    return null;
  }

  const resolvedPageId = question.pageId ?? "__unassigned";
  const pageIndex = pageIds.indexOf(resolvedPageId);
  return pageIndex === -1 ? null : pageIndex;
}

export function findPageIndexForTarget(
  targetRef: string,
  pageIds: string[],
): number | null {
  const pageIndex = pageIds.indexOf(targetRef);
  if (pageIndex !== -1) {
    return pageIndex;
  }

  return null;
}

export function evaluatePageJump(params: {
  page: FormPage;
  pageQuestions: FormQuestion[];
  answers: AnswerRecord;
  jumpEvaluator: JumpLogicEvaluator;
}): string | null {
  const { page, pageQuestions, answers, jumpEvaluator } = params;

  if (!page.jumpLogic || page.jumpLogic.length === 0) {
    return null;
  }

  const sortedRules = [...page.jumpLogic].sort(
    (a, b) => b.priority - a.priority,
  );

  for (const rule of sortedRules) {
    if (!rule.conditions || rule.conditions.length === 0) {
      continue;
    }

    const matches = rule.conditions.map((condition) =>
      evaluateConditionAgainstPage(
        condition,
        pageQuestions,
        answers,
        jumpEvaluator,
      ),
    );

    const shouldMatchAll = rule.match === "all";
    const didMatch = shouldMatchAll
      ? matches.every(Boolean)
      : matches.some(Boolean);
    if (didMatch) {
      return rule.target;
    }
  }

  return null;
}

function evaluateConditionAgainstPage(
  condition: JumpCondition,
  pageQuestions: FormQuestion[],
  answers: AnswerRecord,
  jumpEvaluator: JumpLogicEvaluator,
): boolean {
  if (condition.operator === "always") {
    return true;
  }

  if (!condition.questionRef) {
    return false;
  }

  const question = pageQuestions.find(
    (candidate) => candidate.ref === condition.questionRef,
  );
  if (!question) {
    return false;
  }

  return jumpEvaluator.evaluateConditionForQuestion(
    condition,
    question,
    answers[question.ref],
    answers,
  );
}
